/** Particle/visual density scaling for the reducedMotion accessibility setting. Pure, no Babylon. */

export const PARTICLE_REDUCED_FACTOR = 0.35;
export const LARGE_VISUAL_REDUCED_SCALE = 0.5;

export function particleDensityFactor(reducedMotion: boolean): number {
  return reducedMotion ? PARTICLE_REDUCED_FACTOR : 1.0;
}

export function largeVisualScale(reducedMotion: boolean): number {
  return reducedMotion ? LARGE_VISUAL_REDUCED_SCALE : 1.0;
}

// ---------------------------------------------------------------------------
// ParticleSystemBudget — pure governance seam (no Babylon, no runtime change)
// ---------------------------------------------------------------------------

/**
 * Per-ability peak-capacity table.
 *
 * Each entry reflects the maximum `count` used in AbilityVFX.ts for that
 * ability id (some abilities fire multiple bursts; all are listed separately).
 * These numbers are READ-ONLY in production: changing them here changes the
 * enforced budget ceiling, not the runtime emit behaviour.
 *
 * Abilities that fire more than one burst (resonantDrop, turnTableTouch) list
 * each burst as a distinct sub-key so the sum is correct.
 */
const ABILITY_CAPACITIES: Readonly<Record<string, number>> = Object.freeze({
  turnTableTouch_hit:       46,  // 22 + max(combo=6)*4
  turnTableTouch_miss:      14,
  overclockedLeap:          40,
  auraSight:                50,
  pneumaticAnchor:          18,
  calmingResonance:         18,  // purrPulse burst
  resonantDrop_emerald:     80,
  resonantDrop_copper:      50,
  chronosBubble:           100,
});

/**
 * Return the configured peak-active capacity for one ability burst slot,
 * scaling by reducedMotion if requested.
 *
 * QA overrides (set via _qa_setCapacityOverrides) take precedence over the
 * built-in ABILITY_CAPACITIES table; reducedMotion scaling is still applied.
 *
 * This is a pure read: it does NOT affect any runtime ParticleSystem.
 */
export function getCapacityFor(abilityId: string, reducedMotion = false): number {
  const base = _overrides[abilityId] ?? ABILITY_CAPACITIES[abilityId];
  if (base === undefined) return 0;
  const factor = particleDensityFactor(reducedMotion);
  return Math.max(1, Math.ceil(base * factor));
}

/**
 * Return the sum of peak-active capacities across all tracked ability burst
 * slots, optionally scaled by reducedMotion.
 *
 * This is the number the stress test guards: it must stay ≤ PARTICLE_BUDGET_CEILING.
 */
export function getTotalActiveCapacity(reducedMotion = false): number {
  const factor = particleDensityFactor(reducedMotion);
  let total = 0;
  for (const id of Object.keys(ABILITY_CAPACITIES)) {
    const base = _overrides[id] ?? ABILITY_CAPACITIES[id]!;
    total += Math.max(1, Math.ceil(base * factor));
  }
  return total;
}

/**
 * The hard ceiling on summed peak-active particle capacity.
 * Must be ABOVE the current real total (416 full / 149 reduced) with headroom.
 *
 * If getTotalActiveCapacity() ever approaches this value a future PR
 * will need to either justify the increase or reduce per-ability capacities.
 */
export const PARTICLE_BUDGET_CEILING = 2000;

// ---------------------------------------------------------------------------
// QA-only override hook (locked after first setup call)
// ---------------------------------------------------------------------------

let _overrideSealed = false;
let _overrides: Record<string, number> = {};

/**
 * Set per-ability overrides for QA stress tests.
 * Must be called BEFORE seal(); throws if called after.
 * Never call from production code.
 *
 * @internal
 */
export function _qa_setCapacityOverrides(overrides: Record<string, number>): void {
  if (_overrideSealed) throw new Error("ParticleSystemBudget overrides are sealed");
  _overrides = { ...overrides };
}

/**
 * Seal the override map — no further mutations permitted.
 * Call once at game startup (or at test teardown after QA use).
 *
 * @internal
 */
export function _qa_sealOverrides(): void {
  _overrideSealed = true;
  Object.freeze(_overrides);
}

/**
 * Reset overrides (for test teardown only; production code must not call this).
 *
 * @internal
 */
export function _qa_resetOverrides(): void {
  _overrideSealed = false;
  _overrides = {};
}
